// Authoritative catalogue mirror of core-entities AllPermissions (BEZ-5876+).
// Used when the caller has access_all_sellers and may grant any catalogue permission.
export const ALL_PERMISSION_CODES = [
	"bulk_operation_cancel_orders",
	"bulk_operation_hide_skus",
	"edit_courier_configuration",
	"create_order",
	"import_orders",
	"view_inventory_detail",
	"edit_order",
	"edit_order_copy_split_cancel",
	"create_return_label",
	"view_proof_of_delivery",
	"view_home_page",
	"view_order_summary",
	"view_inventory_summary",
	"view_asn_summary",
	"view_returns_summary",
	"view_invoices",
	"view_reports",
	"view_invoices_internal",
	"create_invoice",
	"upload_invoice",
	"view_settings",
	"import_products",
	"view_debug_page",
	"view_help_centre",
	"raise_a_ticket",
	"view_raise_a_ticket",
	"approve_asn_delivery_slot_request",
	"reject_asn_delivery_slot_request",
	"view_order_claims",
	"view_order",
	"release_pack_and_hold_order",
	"force_order_sync",
	"force_asn_sync",
	"force_product_sync",
	"force_stock_level_sync",
	"force_return_sync",
	"view_audit_logs",
	"create_product",
	"access_frozen_seller",
	"view_extra_order_details",
	"view_extra_asn_details",
	"update_product_status",
	"view_bulk_actions",
	"view_all_products_detail",
	"create_user_seller_role",
	"create_order_claim",
	"raise_claim",
	"create_stock_transfer_request",
	"edit_order_comment",
	"access_all_sellers",
	"manage_seller_roles",
];

/** Short plain-English meaning for each catalogue permission (role UI). */
export const PERMISSION_DESCRIPTIONS = {
	bulk_operation_cancel_orders: "Cancel many orders at once from the orders list",
	bulk_operation_hide_skus: "Hide many products at once from the products list",
	edit_courier_configuration: "Edit seller courier / shipping configuration (Bezos)",
	create_order: "Create orders manually in the portal",
	import_orders: "Import orders from a file",
	view_inventory_detail: "Open full inventory / stock detail for a product",
	edit_order: "Edit order details (address, lines, etc.)",
	edit_order_copy_split_cancel: "Copy, split, or cancel an order",
	create_return_label: "Create a return shipping label for an order",
	view_proof_of_delivery: "View proof of delivery on an order",
	view_home_page: "Open the portal home page",
	view_order_summary: "Legacy alias for orders access (treated as view_order by the API)",
	view_inventory_summary: "Open the inventory / stock summary pages",
	view_asn_summary: "Open the goods-in / ASN summary pages",
	view_returns_summary: "Open the returns summary pages",
	view_invoices: "View seller invoices",
	view_reports: "View and download reports",
	view_invoices_internal: "View internal Bezos invoice screens",
	create_invoice: "Create invoices",
	upload_invoice: "Upload invoice files",
	view_settings: "Open Settings",
	import_products: "Import products from a file",
	view_debug_page: "Open debug tooling pages (Bezos)",
	view_help_centre: "Open the help centre",
	raise_a_ticket: "Submit a support ticket",
	view_raise_a_ticket: "Open the raise-a-ticket page",
	approve_asn_delivery_slot_request: "Approve a requested ASN delivery slot",
	reject_asn_delivery_slot_request: "Reject a requested ASN delivery slot",
	view_order_claims: "View order claims",
	view_order: "Open the orders list and order detail pages",
	release_pack_and_hold_order: "Release a pack-and-hold order",
	force_order_sync: "Force-sync an order with the warehouse (Bezos)",
	force_asn_sync: "Force-sync an ASN with the warehouse (Bezos)",
	force_product_sync: "Force-sync a product with the warehouse (Bezos)",
	force_stock_level_sync: "Force-sync stock levels with the warehouse (Bezos)",
	force_return_sync: "Force-sync a return with the warehouse (Bezos)",
	view_audit_logs: "View audit logs",
	create_product: "Create products in the portal",
	access_frozen_seller: "Access sellers that are frozen (Bezos)",
	view_extra_order_details: "See extra Bezos-only fields on orders",
	view_extra_asn_details: "See extra Bezos-only fields on ASNs",
	update_product_status: "Change product status without raising a ticket",
	view_bulk_actions: "See bulk action controls on list pages",
	view_all_products_detail: "View all product detail (including restricted fields)",
	create_user_seller_role: "Create, remove, and change roles for seller users",
	create_order_claim: "Create an order claim",
	raise_claim: "Raise a claim (legacy / alternate claim flow)",
	create_stock_transfer_request: "Create a stock transfer request",
	edit_order_comment: "Add or edit comments on an order",
	access_all_sellers: "Switch between all sellers (Bezos elevation)",
	manage_seller_roles: "Create and edit custom roles and their permissions",
};

export const CREATE_USER_PERMISSION = "create_user_seller_role";
export const MANAGE_ROLES_PERMISSION = "manage_seller_roles";
export const ACCESS_ALL_SELLERS_PERMISSION = "access_all_sellers";

export function permissionDescription(code) {
	return PERMISSION_DESCRIPTIONS[code] || code;
}

/** Permissions the caller may grant on a custom role (seller ⊆ own; elevated = catalogue). */
export function grantablePermissions(userdata) {
	const perms = userdata?.permissions;
	if (!perms) {
		return [];
	}
	if (perms.has(ACCESS_ALL_SELLERS_PERMISSION)) {
		return [...ALL_PERMISSION_CODES];
	}
	return Array.from(perms.keys()).sort();
}

/**
 * Split a role's permissions into ones the editor can toggle vs ones they cannot grant.
 * Edit save must only send `editable` (API rejects codes outside what the caller may grant).
 */
export function partitionRolePermissions(rolePermissions, grantable) {
	const grantableSet = new Set(grantable || []);
	const editable = [];
	const locked = [];
	(rolePermissions || []).forEach((code) => {
		if (grantableSet.has(code)) {
			editable.push(code);
		} else {
			locked.push(code);
		}
	});
	editable.sort();
	locked.sort();
	return { editable, locked };
}
